import { forwardRef } from 'react';

import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  ghost: 'btn-ghost',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
}

export const Button = ({ variant = 'primary', className, ...props }: ButtonProps) => (
  <button {...props} className={cn(BUTTON_VARIANTS[variant], className)} />
);

export type CardProps = React.HTMLAttributes<HTMLDivElement>;

export const Card = ({ className, ...props }: CardProps) => <div {...props} className={cn('card', className)} />;

export type PanelProps = React.HTMLAttributes<HTMLDivElement>;

export const Panel = ({ className, ...props }: PanelProps) => <div {...props} className={cn('panel', className)} />;

export type PanelHeaderProps = React.HTMLAttributes<HTMLDivElement>;

export const PanelHeader = ({ className, ...props }: PanelHeaderProps) => (
  <div {...props} className={cn('panel-header', className)} />
);

export type PanelBodyProps = React.HTMLAttributes<HTMLDivElement>;

export const PanelBody = ({ className, ...props }: PanelBodyProps) => (
  <div {...props} className={cn('panel-body', className)} />
);

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: 'default' | 'completed' | 'active' | 'paused' | 'archived';
}

const BADGE_TONE_CLASS: Record<Exclude<BadgeProps['tone'], undefined>, string> = {
  default: 'badge',
  completed: 'status-badge--completed',
  active: 'status-badge--active',
  paused: 'status-badge--paused',
  archived: 'status-badge--archived',
};

export const Badge = ({ tone = 'default', className, ...props }: BadgeProps) => (
  <span {...props} className={cn(BADGE_TONE_CLASS[tone], className)} />
);

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...props },
  ref,
) {
  return <input ref={ref} {...props} className={cn('input', className)} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return <textarea ref={ref} {...props} className={cn('textarea', className)} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, ...props },
  ref,
) {
  return <select ref={ref} {...props} className={cn('select', className)} />;
});

export const VisuallyHidden = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      position: 'absolute',
      border: 0,
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      wordWrap: 'normal',
    }}
  >
    {children}
  </span>
);
